import { addDays, differenceInDays, plainDate, todayInAppZone, } from '../date/index.js';
/**
 * Leitura de boleto brasileiro, a partir da linha digitavel ou do codigo de
 * barras.
 *
 * Puro e sem I/O de proposito: e a mesma funcao que o aplicativo vai usar
 * offline, com a camera apontada para o boleto, sem rede. Tudo que depende de
 * banco de dados fica na API.
 *
 * O sistema NAO paga boleto. Isto aqui le o papel para preencher um
 * lembrete de conta a pagar — valor, vencimento e banco emissor. Quem paga e
 * a pessoa, no aplicativo do banco dela.
 *
 * Sao dois formatos diferentes, e confundi-los da valor errado:
 *
 *  - TITULO BANCARIO: 47 digitos na linha digitavel, 44 no codigo de barras.
 *    Traz fator de vencimento e valor;
 *  - ARRECADACAO (concessionaria, tributo, convenio): 48 digitos na linha,
 *    44 no codigo, comeca com 8. NAO traz vencimento — a data mora no campo
 *    livre, em formato que cada segmento define do seu jeito.
 */
export class BoletoError extends Error {
    constructor(message) {
        super(message);
        this.name = 'BoletoError';
    }
}
// ------------------------------------------------------- digitos de controle
/**
 * Modulo 10. Pesos 2 e 1 alternados da direita para a esquerda; produto com
 * dois digitos vira a soma deles.
 */
export function mod10(digits) {
    let sum = 0;
    let weight = 2;
    for (let index = digits.length - 1; index >= 0; index -= 1) {
        const product = Number(digits[index]) * weight;
        sum += product > 9 ? product - 9 : product;
        weight = weight === 2 ? 1 : 2;
    }
    const remainder = sum % 10;
    return remainder === 0 ? 0 : 10 - remainder;
}
/**
 * Modulo 11 com pesos 2 a 9 ciclicos, da direita para a esquerda.
 *
 * Resultado 0, 10 ou 11 vira 1 — regra da FEBRABAN, nao arredondamento nosso:
 * o codigo de barras so tem uma casa para o digito, entao 10 nao caberia, e 0
 * e reservado.
 */
export function mod11Barcode(digits) {
    let sum = 0;
    let weight = 2;
    for (let index = digits.length - 1; index >= 0; index -= 1) {
        sum += Number(digits[index]) * weight;
        weight = weight === 9 ? 2 : weight + 1;
    }
    const digit = 11 - (sum % 11);
    return digit === 0 || digit > 9 ? 1 : digit;
}
/** Modulo 11 da arrecadacao: aqui 0 e 1 sao possiveis, e 10 vira 0. */
export function mod11Arrecadacao(digits) {
    let sum = 0;
    let weight = 2;
    for (let index = digits.length - 1; index >= 0; index -= 1) {
        sum += Number(digits[index]) * weight;
        weight = weight === 9 ? 2 : weight + 1;
    }
    const remainder = sum % 11;
    const digit = 11 - remainder;
    if (remainder === 0)
        return 0;
    if (remainder === 1)
        return 0;
    return digit;
}
// -------------------------------------------------------- fator de vencimento
/** Data em que o fator de vencimento passou a valer: fator 1000 = 03/07/2000. */
const FATOR_EPOCH_1 = plainDate('2000-07-03');
/**
 * O fator estourou e recomecou.
 *
 * O campo tem quatro digitos e vai de 1000 a 9999. O 9999 caiu em 21/02/2025,
 * e a FEBRABAN definiu que no dia seguinte o contador volta para 1000. Isso
 * torna a data AMBIGUA no proprio formato: o fator 1000 pode ser 03/07/2000
 * ou 22/02/2025, e o papel nao diz qual.
 *
 * A desambiguacao aqui e por proximidade da data de referencia. Um boleto de
 * 2000 nao esta na mao de ninguem hoje; um de 2025 esta.
 */
const FATOR_EPOCH_2 = plainDate('2025-02-22');
export function fatorToDueDate(fator, reference) {
    // 0000 nao e erro: e boleto emitido sem vencimento definido.
    if (fator === 0)
        return { date: null, note: null };
    if (fator < 1000 || fator > 9999) {
        throw new BoletoError(`Fator de vencimento fora da faixa valida: ${fator}`);
    }
    const hoje = reference ?? todayInAppZone();
    const offset = fator - 1000;
    const primeiro = addDays(FATOR_EPOCH_1, offset);
    const segundo = addDays(FATOR_EPOCH_2, offset);
    const distancia1 = Math.abs(differenceInDays(primeiro, hoje));
    const distancia2 = Math.abs(differenceInDays(segundo, hoje));
    if (distancia1 <= distancia2)
        return { date: primeiro, note: null };
    return {
        date: segundo,
        note: 'O fator de vencimento reiniciou em 22/02/2025 e a data ficou ambigua no proprio boleto. Confira o vencimento impresso.',
    };
}
export function dueDateToFator(date, reference) {
    const hoje = reference ?? todayInAppZone();
    // `differenceInDays(de, para)` cresce quando `para` e mais tarde: estar
    // depois da virada e o resultado ser POSITIVO, nao negativo.
    const epoch = differenceInDays(FATOR_EPOCH_2, hoje) >= 0 ? FATOR_EPOCH_2 : FATOR_EPOCH_1;
    const fator = 1000 + differenceInDays(epoch, date);
    if (fator < 1000 || fator > 9999) {
        throw new BoletoError(`Data fora da faixa que o fator de vencimento representa: ${date}`);
    }
    return fator;
}
// ------------------------------------------------------------------ conversao
export function onlyDigits(input) {
    return input.replace(/\D/g, '');
}
/**
 * Linha digitavel de titulo bancario (47) para codigo de barras (44).
 *
 * A linha nao e o codigo com pontinhos: os campos foram EMBARALHADOS, e o
 * digito verificador geral, que no codigo fica na quinta posicao, na linha
 * fica sozinho no quarto campo.
 */
export function bancarioLinhaToBarcode(linha) {
    if (linha.length !== 47) {
        throw new BoletoError(`Linha digitavel de titulo tem 47 digitos, veio com ${linha.length}.`);
    }
    const bancoMoeda = linha.slice(0, 4);
    const dvGeral = linha.slice(32, 33);
    const fatorValor = linha.slice(33, 47);
    const campoLivre = linha.slice(4, 9) + linha.slice(10, 20) + linha.slice(21, 31);
    return bancoMoeda + dvGeral + fatorValor + campoLivre;
}
export function bancarioBarcodeToLinha(barcode) {
    if (barcode.length !== 44) {
        throw new BoletoError(`Codigo de barras tem 44 digitos, veio com ${barcode.length}.`);
    }
    const banco = barcode.slice(0, 3);
    const moeda = barcode.slice(3, 4);
    const dvGeral = barcode.slice(4, 5);
    const fatorValor = barcode.slice(5, 19);
    const campoLivre = barcode.slice(19, 44);
    const campo1 = banco + moeda + campoLivre.slice(0, 5);
    const campo2 = campoLivre.slice(5, 15);
    const campo3 = campoLivre.slice(15, 25);
    return (campo1 +
        String(mod10(campo1)) +
        campo2 +
        String(mod10(campo2)) +
        campo3 +
        String(mod10(campo3)) +
        dvGeral +
        fatorValor);
}
export function parseBoleto(input, options = {}) {
    const digits = onlyDigits(input);
    if (digits.length === 48)
        return parseArrecadacaoLinha(digits, options);
    if (digits.length === 47)
        return parseBancarioLinha(digits, options);
    if (digits.length === 44) {
        return digits.startsWith('8')
            ? parseArrecadacaoBarcode(digits, options)
            : parseBancarioBarcode(digits, options);
    }
    throw new BoletoError(`Nao reconheci: um boleto tem 47 digitos (titulo), 48 (arrecadacao) ou 44 (codigo de barras). Vieram ${digits.length}.`);
}
function parseBancarioLinha(linha, options) {
    if (!options.allowInvalidCheckDigits) {
        const campos = [
            [linha.slice(0, 9), linha.slice(9, 10)],
            [linha.slice(10, 20), linha.slice(20, 21)],
            [linha.slice(21, 31), linha.slice(31, 32)],
        ];
        campos.forEach(([campo, dv], index) => {
            if (String(mod10(campo)) !== dv) {
                throw new BoletoError(`O ${index + 1}o campo da linha digitavel nao confere. Confira se algum digito foi trocado.`);
            }
        });
    }
    return parseBancarioBarcode(bancarioLinhaToBarcode(linha), options, linha);
}
function parseBancarioBarcode(barcode, options, linha) {
    const semDv = barcode.slice(0, 4) + barcode.slice(5, 44);
    const dvGeral = barcode.slice(4, 5);
    if (!options.allowInvalidCheckDigits && String(mod11Barcode(semDv)) !== dvGeral) {
        throw new BoletoError('O digito verificador geral do boleto nao confere. Confira se algum digito foi trocado.');
    }
    const fator = Number(barcode.slice(5, 9));
    const valor = BigInt(barcode.slice(9, 19));
    const { date, note } = fatorToDueDate(fator, options.reference);
    return {
        kind: 'BANCARIO',
        barcode,
        digitableLine: linha ?? bancarioBarcodeToLinha(barcode),
        bankCode: barcode.slice(0, 3),
        dueDate: date,
        // Valor zerado e boleto em branco (o cedente preenche), nao R$ 0,00.
        amountCents: valor === 0n ? null : valor,
        segment: null,
        dueDateNote: note,
        amountNote: null,
    };
}
/**
 * Arrecadacao: 48 digitos em quatro blocos de 11 mais um digito cada.
 *
 * O codigo de barras por tras deles e:
 *   8 | segmento(1) | identificador de valor(1) | DV(1) | valor(11) | livre(29)
 *
 * O terceiro digito (identificador de valor) manda em duas coisas, e ignorar
 * a segunda da valor errado. Conforme o layout da FEBRABAN:
 *
 *   '6' — valor a ser cobrado efetivamente em reais, DV modulo 10
 *   '7' — quantidade de moeda, zeros, ou valor a reajustar por indice, mod 10
 *   '8' — valor a ser cobrado efetivamente em reais, DV modulo 11
 *   '9' — quantidade de moeda, zeros, ou valor a reajustar por indice, mod 11
 *
 * Ou seja: em 7 e 9 os onze digitos NAO sao reais. Le-los como centavos
 * preencheria a tela com um numero que nao e dinheiro.
 */
function parseArrecadacaoLinha(linha, options) {
    const identificador = Number(linha[2]);
    const usaMod10 = identificador === 6 || identificador === 7;
    const blocos = [
        [linha.slice(0, 11), linha.slice(11, 12)],
        [linha.slice(12, 23), linha.slice(23, 24)],
        [linha.slice(24, 35), linha.slice(35, 36)],
        [linha.slice(36, 47), linha.slice(47, 48)],
    ];
    if (!options.allowInvalidCheckDigits) {
        blocos.forEach(([bloco, dv], index) => {
            const esperado = usaMod10 ? mod10(bloco) : mod11Arrecadacao(bloco);
            if (String(esperado) !== dv) {
                throw new BoletoError(`O ${index + 1}o bloco da linha digitavel nao confere. Confira se algum digito foi trocado.`);
            }
        });
    }
    const barcode = blocos.map(([bloco]) => bloco).join('');
    return parseArrecadacaoBarcode(barcode, options);
}
function parseArrecadacaoBarcode(barcode, options) {
    const identificador = Number(barcode[2]);
    const dvGeral = barcode.slice(3, 4);
    const semDv = barcode.slice(0, 3) + barcode.slice(4, 44);
    if (!options.allowInvalidCheckDigits) {
        const esperado = identificador === 6 || identificador === 7 ? mod10(semDv) : mod11Arrecadacao(semDv);
        if (String(esperado) !== dvGeral) {
            throw new BoletoError('O digito verificador do codigo de arrecadacao nao confere. Confira se algum digito foi trocado.');
        }
    }
    // Em 7 e 9 o campo e referencia, nao reais: devolver nulo e pedir o valor
    // e melhor do que exibir um numero que parece dinheiro e nao e.
    const valorEmReais = identificador === 6 || identificador === 8;
    const valor = BigInt(barcode.slice(4, 15));
    return {
        kind: 'ARRECADACAO',
        barcode,
        digitableLine: arrecadacaoBarcodeToLinha(barcode),
        bankCode: null,
        // A arrecadacao nao carrega vencimento no codigo. Quem preenche e a
        // pessoa, lendo o papel — e por isso a tela pede a data.
        dueDate: null,
        amountCents: valorEmReais && valor !== 0n ? valor : null,
        segment: Number(barcode[1]),
        dueDateNote: null,
        amountNote: valorEmReais
            ? null
            : 'este boleto traz quantidade de moeda ou valor a reajustar, nao reais: informe o valor do papel',
    };
}
export function arrecadacaoBarcodeToLinha(barcode) {
    if (barcode.length !== 44) {
        throw new BoletoError(`Codigo de barras tem 44 digitos, veio com ${barcode.length}.`);
    }
    const identificador = Number(barcode[2]);
    const usaMod10 = identificador === 6 || identificador === 7;
    let linha = '';
    for (let index = 0; index < 4; index += 1) {
        const bloco = barcode.slice(index * 11, index * 11 + 11);
        const dv = usaMod10 ? mod10(bloco) : mod11Arrecadacao(bloco);
        linha += bloco + String(dv);
    }
    return linha;
}
/** Pontuacao que a pessoa reconhece do papel. */
export function formatDigitableLine(digits) {
    if (digits.length === 47) {
        return [
            `${digits.slice(0, 5)}.${digits.slice(5, 10)}`,
            `${digits.slice(10, 15)}.${digits.slice(15, 21)}`,
            `${digits.slice(21, 26)}.${digits.slice(26, 32)}`,
            digits.slice(32, 33),
            digits.slice(33, 47),
        ].join(' ');
    }
    if (digits.length === 48) {
        return [
            digits.slice(0, 12),
            digits.slice(12, 24),
            digits.slice(24, 36),
            digits.slice(36, 48),
        ].join(' ');
    }
    return digits;
}
/** Nomes dos bancos que aparecem em boleto no dia a dia. */
const BANK_NAMES = {
    '001': 'Banco do Brasil',
    '033': 'Santander',
    '041': 'Banrisul',
    '077': 'Inter',
    '104': 'Caixa Economica Federal',
    '237': 'Bradesco',
    '260': 'Nu Pagamentos',
    '336': 'C6 Bank',
    '341': 'Itau',
    '356': 'Banco Real',
    '399': 'HSBC',
    '422': 'Safra',
    '655': 'Votorantim',
    '745': 'Citibank',
    '748': 'Sicredi',
    '756': 'Sicoob',
};
export function bankNameFor(code) {
    return code ? (BANK_NAMES[code] ?? null) : null;
}
/** Segmentos da arrecadacao, conforme a FEBRABAN. */
const SEGMENT_NAMES = {
    1: 'Prefeitura',
    2: 'Saneamento',
    3: 'Energia eletrica ou gas',
    4: 'Telecomunicacoes',
    5: 'Orgao governamental',
    6: 'Carne ou convenio',
    7: 'Multa de transito',
    9: 'Uso exclusivo do banco',
};
export function segmentNameFor(segment) {
    return segment === null ? null : (SEGMENT_NAMES[segment] ?? null);
}
//# sourceMappingURL=index.js.map